/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { Iconify } from 'src/components';

const FUNNEL_STAGES = [
    {
        key: 'totalWhatsAppLeads',
        label: 'WhatsApp Leads',
        color: '#25D366',
        icon: 'ic:baseline-whatsapp',
    },
    {
        key: 'consultationsBooked',
        label: 'Consultations Booked',
        color: '#3b82f6',
        icon: 'mdi:calendar-check',
    },
    {
        key: 'treatmentStarted',
        label: 'Treatment Started',
        color: '#f59e0b',
        icon: 'mdi:tooth',
    },
];

const WhatsAppConversionFunnel = ({ data }) => {
    const maxValue = Math.max(data.totalWhatsAppLeads || 1, 1);

    const calculateConversion = (from, to) => {
        if (from === 0) return 0;
        return ((to / from) * 100).toFixed(1);
    };

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                mb: 3,
            }}
        >
            {/* Header */}
            <Typography sx={{ fontSize: '20px', fontWeight: 600, mb: 3 }}>
                WhatsApp Lead Conversion
            </Typography>

            {/* Funnel */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {FUNNEL_STAGES.map((stage, index) => {
                    const value = data[stage.key] || 0;
                    const barWidth = (value / maxValue) * 100;
                    const prevStage = index > 0 ? FUNNEL_STAGES[index - 1] : null;
                    const prevValue = prevStage ? data[prevStage.key] || 0 : 0;
                    const conversionRate = prevStage ? calculateConversion(prevValue, value) : null;

                    return (
                        <Box key={stage.key}>
                            {/* Conversion arrow between stages */}
                            {conversionRate !== null && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        py: 1,
                                        color: '#64748b',
                                    }}
                                >
                                    <Iconify icon="mdi:arrow-down" width={20} height={20} />
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            ml: 0.5,
                                        }}
                                    >
                                        {conversionRate}% conversion
                                    </Typography>
                                </Box>
                            )}

                            {/* Stage bar */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                {/* Icon */}
                                <Box
                                    sx={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        bgcolor: `${stage.color}15`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Iconify
                                        icon={stage.icon}
                                        width={22}
                                        height={22}
                                        sx={{ color: stage.color }}
                                    />
                                </Box>

                                {/* Bar */}
                                <Box sx={{ flex: 1 }}>
                                    <Box
                                        sx={{
                                            height: '48px',
                                            bgcolor: `${stage.color}15`,
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: `${Math.max(barWidth, 5)}%`,
                                                height: '100%',
                                                bgcolor: stage.color,
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                px: 2,
                                                transition: 'width 0.5s ease',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {stage.label}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: '16px',
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {value}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Summary */}
            {data.totalWhatsAppLeads > 0 && (
                <Box
                    sx={{
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 4,
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                            Overall Conversion
                        </Typography>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#25D366' }}>
                            {calculateConversion(data.totalWhatsAppLeads, data.treatmentStarted)}%
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                            Drop-off
                        </Typography>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#ef4444' }}>
                            {data.totalWhatsAppLeads - data.treatmentStarted}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default WhatsAppConversionFunnel;

WhatsAppConversionFunnel.propTypes = {
    data: PropTypes.shape({
        totalWhatsAppLeads: PropTypes.number,
        consultationsBooked: PropTypes.number,
        treatmentStarted: PropTypes.number,
    }),
};

WhatsAppConversionFunnel.defaultProps = {
    data: {
        totalWhatsAppLeads: 0,
        consultationsBooked: 0,
        treatmentStarted: 0,
    },
};
